import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BadgeFacture, DialogFacture, fmt2 } from '@/components/lmds/ApercuFacture';

const API = process.env.REACT_APP_BACKEND_URL;

// 🧾 Phase 6 : onglet « Factures » de la Gestion des installateurs (tous les installateurs).
export function FacturesInstallateurs() {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const [statut, setStatut] = useState('');
  const [instId, setInstId] = useState('');
  const [data, setData] = useState({ factures: [], total: 0 });
  const [anomalies, setAnomalies] = useState([]);
  const [dry, setDry] = useState(null);
  const [ouverte, setOuverte] = useState(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const q = new URLSearchParams({ annee });
    if (statut) q.set('statut', statut);
    if (instId) q.set('installateur_id', instId);
    axios.get(`${API}/api/installateurs/factures?${q}`).then((r) => setData(r.data)).catch(() => toast.error('Impossible de charger les factures.'));
    axios.get(`${API}/api/installateurs/factures/anomalies`).then((r) => setAnomalies(r.data.anomalies || [])).catch(() => {});
  }, [annee, statut, instId, version]);
  const installateurs = [...new Map(data.factures.map((f) => [f.installateur_id, f.installateur_nom])).entries()];
  const historique = async (dryRun) => {
    if (!dryRun && !window.confirm(`Générer ${dry?.a_creer?.length || 0} facture(s) historique(s) pour ${annee} ? Cette action émet des factures réelles (mois déjà payés).`)) return;
    try {
      const r = await axios.post(`${API}/api/installateurs/factures/generer-historique`, { annee, dry_run: dryRun });
      if (dryRun) setDry(r.data);
      else { setDry(null); toast.success(`${r.data.resultat.emises.length} facture(s) émise(s), ${r.data.resultat.anomalies.length} anomalie(s).`); setVersion((v) => v + 1); }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de la génération.'); }
  };
  const annuler = async (f) => {
    const motif = window.prompt(`Motif d'annulation de ${f.numero} :`);
    if (!motif) return;
    try { await axios.post(`${API}/api/installateurs/factures/${f.id}/annuler`, { motif }); toast.success('Facture annulée.'); setVersion((v) => v + 1); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Échec.'); }
  };
  return (
    <div className="space-y-4" data-testid="onglet-factures-gestion">
      {anomalies.length > 0 && (
        <div className="border-2 border-red-500 bg-red-50 rounded p-3 text-sm" data-testid="factures-anomalies">
          <b>{anomalies.length} anomalie(s) de réconciliation</b> — aucune facture n'a été émise pour ces semaines : le montant calculé ne concordait pas avec le rapport.
          <ul className="list-disc ml-5 text-xs mt-1">{anomalies.slice(0, 10).map((a) => <li key={a.id}>{a.installateur_id} — {a.annee} S{a.semaine} : {a.detail}</li>)}</ul>
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <select className="border rounded-md h-9 px-2 text-sm bg-white" value={annee} onChange={(e) => setAnnee(parseInt(e.target.value))} data-testid="factures-gestion-annee">
          {[annee + 1, annee, annee - 1, annee - 2].map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select className="border rounded-md h-9 px-2 text-sm bg-white" value={statut} onChange={(e) => setStatut(e.target.value)} data-testid="factures-gestion-statut">
          <option value="">Tous les statuts</option><option value="emise">Émises</option><option value="remplacee">Remplacées</option><option value="annulee">Annulées</option>
        </select>
        <select className="border rounded-md h-9 px-2 text-sm bg-white" value={instId} onChange={(e) => setInstId(e.target.value)} data-testid="factures-gestion-installateur">
          <option value="">Tous les installateurs</option>{installateurs.map(([id, nom]) => <option key={id} value={id}>{nom}</option>)}
        </select>
        <span className="text-sm text-slate-500" data-testid="factures-gestion-total">{data.total} facture(s)</span>
        <Button size="sm" variant="outline" onClick={() => historique(true)} data-testid="historique-dry-run">Historique {annee} : simuler</Button>
      </div>
      {dry && (
        <div className="border rounded p-3 bg-amber-50 text-sm" data-testid="historique-resultat">
          <b>Simulation</b> : {dry.a_creer.length} facture(s) seraient créées ({dry.ignorees} semaine(s) ignorées : 0 $, mois non payé ou déjà facturées).
          <ul className="text-xs ml-5 list-disc max-h-40 overflow-auto">{dry.a_creer.map((c, i) => <li key={i}>{c.installateur_nom} — S{c.semaine} ({c.annee}) : {fmt2(c.montant_final)}</li>)}</ul>
          {dry.a_creer.length > 0 && <Button size="sm" className="mt-2" onClick={() => historique(false)} data-testid="historique-executer">Exécuter la génération</Button>}
        </div>
      )}
      <table className="w-full text-sm" data-testid="table-factures-gestion">
        <thead><tr className="text-left bg-slate-50"><th className="p-2">Numéro</th><th className="p-2">Installateur</th><th className="p-2">Semaine</th><th className="p-2">Date</th><th className="p-2 text-right">Montant final</th><th className="p-2">Statut</th><th className="p-2">Actions</th></tr></thead>
        <tbody>
          {data.factures.map((f) => (
            <tr key={f.id} className="border-b" data-testid={`gestion-facture-${f.numero}`}>
              <td className="p-2 font-mono">{f.numero}</td><td className="p-2">{f.installateur_nom}</td><td className="p-2">S{f.semaine} {f.annee}</td><td className="p-2">{f.date_facture}</td>
              <td className="p-2 text-right font-semibold">{fmt2(f.montant_final)}</td><td className="p-2"><BadgeFacture f={f} /></td>
              <td className="p-2 flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setOuverte(f)} data-testid={`gestion-facture-voir-${f.numero}`}>Voir</Button>
                {f.statut === 'emise' && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => annuler(f)} data-testid={`gestion-facture-annuler-${f.numero}`}>Annuler</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!data.factures.length && <p className="text-sm text-slate-500" data-testid="factures-gestion-vide">Aucune facture pour ces filtres.</p>}
      <DialogFacture installateurId={ouverte?.installateur_id} factureId={ouverte?.id || null} onFermer={() => setOuverte(null)} />
    </div>
  );
}
